import { sendGTMEvent } from '@next/third-parties/google'
import { parseGid } from '@shopify/hydrogen-react'

import { AnalyticsEvent, DEFAULT_CURRENCY } from './Analytics.constants'

import type { CartReturnType, Items, ProductReturnType } from './Analytics.types'
import type { CartLine as CartLineType } from '@shopify/hydrogen-react/storefront-api-types'
import type { CurrencyCode } from '@shopify/hydrogen-react/storefront-api-types'

export const getCartValue = (cartLines: CartLineType[]) => {
  return cartLines.reduce((acc, line) => acc + Number(line.cost.totalAmount.amount || 0), 0)
}

export const gtm = {
  push<E>(event: (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent], ecommerce: E) {
    sendGTMEvent({ ecommerce: null }) // Push a null event to reset the ecommerce data
    sendGTMEvent({ event, ecommerce })
  },
}

export const getGtmItems = (lines: CartLineType[]) => {
  if (!lines || lines.length === 0) return []

  return lines.map((line) => {
    const quantity = line.quantity
    const totalAmount = Number(line.cost.totalAmount.amount || 0)
    const amountPerItem = totalAmount / quantity
    const { id: productId } = parseGid(line.merchandise.product.id || '')
    const { id: variantId } = parseGid(line.merchandise.id || '')
    const title = line.merchandise.product.title || ''

    return {
      item_id: productId,
      item_name: title,
      item_variant: variantId,
      price: amountPerItem,
      quantity,
    }
  })
}

export const getAddedAndRemovedItems = (currentCartLines: CartLineType[], previousCartLines: CartLineType[]) => {
  const itemsAdded: CartLineType[] = []
  const itemsRemoved: CartLineType[] = []

  // Detect quantity changes and missing cart lines
  previousCartLines.forEach((prevLine) => {
    const matchedLineId = currentCartLines.filter((line) => prevLine.id === line.id)
    if (matchedLineId?.length === 1) {
      const matchedLine = matchedLineId[0]
      if (prevLine.quantity < matchedLine.quantity) {
        itemsAdded.push(prevLine)
      } else if (prevLine.quantity > matchedLine.quantity) {
        itemsRemoved.push(matchedLine)
      }
    } else {
      itemsRemoved.push(prevLine)
    }
  })

  // Detect added to cart
  currentCartLines.forEach((line) => {
    const matchedLineId = previousCartLines.filter((previousLine) => line.id === previousLine.id)
    if (!matchedLineId || matchedLineId.length === 0) {
      itemsAdded.push(line)
    }
  })

  return { itemsAdded, itemsRemoved }
}

export const buildCollectionPayload = ({ items, collectionId, collectionHandle }: { items: Items; collectionId?: string; collectionHandle?: string }) => {
  if (!items || items.length === 0) return

  return {
    currency: items[0]?.price?.currencyCode || DEFAULT_CURRENCY,
    ...(collectionId && { item_list_id: collectionId }),
    ...(collectionHandle && { item_list_name: collectionHandle }),
    items: items.map((item, index) => ({
      item_id: item.id,
      item_name: item.title,
      index,
      price: Number(item.price?.amount || 0),
      item_brand: item.vendor,
      ...(item.collections?.[0] && { item_category: item.collections[0] }),
      ...(item.collections?.[1] && { item_category2: item.collections[1] }),
      ...(item.collections?.[2] && { item_category3: item.collections[2] }),
      ...(item.collections?.[3] && { item_category4: item.collections[3] }),
      ...(item.collections?.[4] && { item_category5: item.collections[4] }),
      item_variant: item.objectID,
    })),
  }
}

export const sendProductViewed = (product: ProductReturnType) => {
  if (!product) return

  const price = Number(product?.variants?.nodes?.[0]?.price?.amount || 0)
  const currency = product?.variants?.nodes?.[0]?.price?.currencyCode || DEFAULT_CURRENCY
  const { id: productId } = parseGid(product?.id || '')
  const { id: variantId } = parseGid(product?.variants?.nodes?.[0]?.id || '')

  const data = {
    currency: currency as CurrencyCode,
    value: price,
    items: [
      {
        item_id: productId,
        item_name: product?.title || '',
        item_brand: product?.vendor || '',
        price,
        ...(variantId && { item_variant: variantId }),
      },
    ],
  }

  gtm.push(AnalyticsEvent.PRODUCT_VIEWED, data)
}

export const sendCartViewed = (cart: CartReturnType) => {
  if (!cart || !cart.lines || cart.lines.length === 0) return

  const items =
    cart?.lines?.map((line) => {
      const quantity = line?.quantity || 0
      const totalAmount = Number(line?.cost?.totalAmount?.amount || 0)
      const amountPerItem = totalAmount / quantity
      const { id: productId } = parseGid(line?.merchandise?.product?.id || '')
      const { id: variantId } = parseGid(line?.merchandise?.id || '')

      return {
        item_id: productId,
        item_name: line?.merchandise?.product?.title || '',
        item_variant: variantId,
        price: amountPerItem,
        quantity,
      }
    }) || []

  gtm.push(AnalyticsEvent.CART_VIEWED, {
    currency: cart?.cost?.totalAmount?.currencyCode || DEFAULT_CURRENCY,
    value: Number(cart?.cost?.totalAmount?.amount || 0),
    items,
  })
}
